/**
 * @license
 * This file is part of Ariakit Plus. For the full license, see
 * https://ariakit.org/plus/license
 */
import * as Ariakit from "@ariakit/react";
import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  KeyReturn,
  MagnifyingGlass,
} from "@phosphor-icons/react";
import { Spinner, Tab, TabList, TabPanel, TabProvider } from "@replicate/ui";
import clsx from "clsx";
import * as React from "react";
import { isMacOs } from "react-device-detect";
import { useHotkeys } from "react-hotkeys-hook";

export interface ComboboxProviderProps extends Ariakit.ComboboxProviderProps {
  tabId?: Ariakit.TabProviderProps["selectedId"];
  setTabId?: (id: string) => void;
  defaultTabId?: Ariakit.TabProviderProps["defaultSelectedId"];
  onSearch?: Ariakit.ComboboxProviderProps["setValue"];
  onTabChange?: (id: string) => void;
}

export function ComboboxProvider({
  tabId,
  setTabId,
  defaultTabId,
  onSearch,
  onTabChange,
  ...props
}: ComboboxProviderProps) {
  return (
    <Ariakit.ComboboxProvider
      {...props}
      // If consumers want to control the state, they should use `setValue`. If
      // a search operation is needed, `onSearch` should be used to ensure the
      // input remains responsive to user input, thanks to startTransition.
      setValue={(value) => {
        props.setValue?.(value);
        React.startTransition(() => {
          onSearch?.(value);
        });
      }}
    >
      <TabProvider
        selectedId={tabId}
        defaultSelectedId={defaultTabId}
        // If consumers want to control the state, they should use `setTabId`.
        // `onTabChange` is best used when they simply want to identify the
        // selected tab so they can update the content without blocking the UI.
        // This approach allows users on lower-end devices to continue
        // interacting with the UI, including switching tabs, even as the
        // content updates.
        setSelectedId={(id) => {
          if (!id) return;
          setTabId?.(id);
          React.startTransition(() => {
            onTabChange?.(id);
          });
        }}
      >
        {props.children}
      </TabProvider>
    </Ariakit.ComboboxProvider>
  );
}

export interface ComboboxProps extends Ariakit.ComboboxProps {}

export const Combobox = React.forwardRef<
  HTMLInputElement,
  ComboboxProps & { loading: boolean }
>(function Combobox({ loading, ...rest }, ref) {
  const combobox = Ariakit.useComboboxContext();
  const isOpen = Ariakit.useStoreState(combobox, "open");

  useHotkeys(
    "mod+k",
    (e) => {
      e.preventDefault();
      if (!combobox) return;
      combobox.toggle();
      combobox.move(null);
    },
    [combobox]
  );

  return (
    <div className="combobox-wrapper relative">
      <div className="absolute left-0 top-0 bottom-0 flex items-center h-full px-2 pointer-events-none text-r8-gray-10">
        <MagnifyingGlass size={15} />
      </div>
      <Ariakit.Combobox
        ref={ref}
        autoSelect
        autoFocus={false}
        autoComplete="none"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck="false"
        maxLength={100}
        {...rest}
        className={clsx("combobox", rest.className)}
      />
      <div className="absolute right-0 top-0 bottom-0 flex items-center h-full px-2 text-r8-gray-10">
        {loading && isOpen ? (
          <div className="size-3">
            <Spinner />
          </div>
        ) : null}
      </div>
      <div
        data-open={isOpen}
        className="hidden md:flex absolute right-2 top-0 bottom-0 items-center font-mono pointer-events-none opacity-100 data-[open=true]:opacity-0 transition-opacity"
      >
        <kbd className="text-[10px] bg-r8-gray-a3 px-1.5 py-0.5 text-r8-gray-11">
          {isMacOs ? "cmd" : "ctrl"}+k
        </kbd>
      </div>
    </div>
  );
});

export interface ComboboxPopoverProps extends Ariakit.ComboboxPopoverProps {}

export const ComboboxPopover = React.forwardRef<
  HTMLDivElement,
  ComboboxPopoverProps
>(function ComboboxPopover(props, ref) {
  const combobox = Ariakit.useComboboxContext();
  const isInputActive = Ariakit.useStoreState(
    combobox,
    (state) => state?.activeId === null
  );
  // React.useDeferredValue helps in maintaining a responsive UI during the
  // mounting of the popover.
  const mounted = React.useDeferredValue(
    Ariakit.useStoreState(combobox, "mounted")
  );
  return (
    <Ariakit.ComboboxPopover
      ref={ref}
      gutter={8}
      unmountOnHide
      fitViewport
      autoFocusOnHide={false}
      className="r8-popover bg-white dark:bg-r8-gray-1 max-h-96 overflow-y-auto h-full z-20 overflow-x-hidden w-[600px] isolate scroll-pb-8 scroll-pt-16"
      hidden={!mounted}
      {...props}
    >
      {mounted && props.children}
      <div className="sticky bottom-0 bg-r8-gray-1 overflow-x-auto shrink-0 p-2 border-t border-r8-gray-6 flex items-center gap-3 isolate z-10">
        <div className="flex items-center gap-0.5">
          <kbd className="kbd" aria-label="Up Arrow">
            <ArrowUp />
          </kbd>
          <kbd className="kbd" aria-label="Down Arrow">
            <ArrowDown />
          </kbd>
          <span className="text-r8-sm text-r8-gray-10">to navigate</span>
        </div>
        {!isInputActive && (
          <div className="flex items-center gap-0.5">
            <kbd className="kbd" aria-label="Left Arrow">
              <ArrowLeft />
            </kbd>
            <kbd className="kbd" aria-label="Right Arrow">
              <ArrowRight />
            </kbd>
            <span className="text-r8-sm text-r8-gray-10">to switch tabs</span>
          </div>
        )}
        {!isInputActive && (
          <div className="flex items-center gap-0.5">
            <kbd className="kbd" aria-label="Enter">
              <KeyReturn />
            </kbd>
            <span className="text-r8-sm text-r8-gray-10">to select</span>
          </div>
        )}
      </div>
    </Ariakit.ComboboxPopover>
  );
});

export interface ComboboxTabListProps extends Ariakit.TabListProps {}

export const ComboboxTabList = React.forwardRef<
  HTMLDivElement,
  ComboboxTabListProps
>(function ComboboxTabList(props, ref) {
  return (
    <TabList
      ref={ref}
      {...props}
      size="sm"
      className={clsx(
        "tab-list px-4 sticky top-0 bg-r8-gray-1 isolate z-10",
        props.className
      )}
    />
  );
});

export interface ComboboxTabProps extends Ariakit.TabProps {}

export const ComboboxTab = React.forwardRef<
  HTMLButtonElement,
  ComboboxTabProps
>(function ComboboxTab(props, ref) {
  return (
    <Tab
      ref={ref}
      accessibleWhenDisabled={false}
      {...props}
      className={clsx("tab", props.className)}
    />
  );
});

export interface ComboboxPanelProps extends Ariakit.TabPanelProps {}

export const ComboboxPanel = React.forwardRef<
  HTMLDivElement,
  ComboboxPanelProps
>(function ComboboxTabPanel(props, ref) {
  const tab = Ariakit.useTabContext();
  // We assume a single tab panel is being displayed with the `tabId` and
  // `children` props varying based on the active tab. If a `tabId` prop isn't
  // supplied, we can deduce it from the selected tab.
  const tabId = Ariakit.useStoreState(
    tab,
    (state) => props.tabId ?? state?.selectedId
  );
  return (
    <TabPanel
      ref={ref}
      tabId={tabId}
      {...props}
      className={clsx("tab-panel", props.className)}
    >
      <Ariakit.ComboboxList className="listbox">
        {props.children}
      </Ariakit.ComboboxList>
    </TabPanel>
  );
});

export interface ComboboxItemProps extends Ariakit.ComboboxItemProps {}

export const ComboboxItem = React.forwardRef<HTMLDivElement, ComboboxItemProps>(
  function ComboboxItem(props, ref) {
    return (
      <Ariakit.ComboboxItem
        ref={ref}
        focusOnHover
        blurOnHoverEnd={false}
        {...props}
        className={clsx("combobox-item", props.className)}
      />
    );
  }
);
